/**
 * Signup
 *
 * Creates a Free account and signs the person straight in, because the
 * moment after someone commits is the worst possible time to send them to
 * their inbox. The confirmation email still goes out; the app asks for it
 * later, in a banner they can dismiss their way through.
 *
 * The panel on the left says what a free account actually gets, in the same
 * numbers the server enforces. Promising more than the plan allows would be
 * found out on the third alert.
 */

import React, { useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Alert, IconButton,
  InputAdornment, LinearProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, East } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useThemeMode, ForceLightTheme } from '../../context/ThemeContext';
import { useAuth, HOME_FOR_ROLE } from '../../context/AuthContext';

const MIN_LENGTH = 8;

const FREE_INCLUDES = [
  'Price alerts on two commodities, emailed to you',
  'A portfolio of three holdings',
  'Three months of market price history',
  'Every public market page',
];

function SignupInner() {
  const { palette } = useThemeMode();
  const { register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(location.search);
  const redirect = params.get('redirect');

  if (isAuthenticated) {
    const dest = redirect ? decodeURIComponent(redirect) : HOME_FOR_ROLE[user.role] || '/';
    return <Navigate to={dest} replace />;
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const tooShort = form.password.length > 0 && form.password.length < MIN_LENGTH;
  const canSubmit = form.name.trim() && form.email.trim() && form.password.length >= MIN_LENGTH && !submitting;

  const strengthScore = (() => {
    const pw = form.password;
    let score = 0;
    if (pw.length >= MIN_LENGTH) score += 1;
    if (pw.length >= 12) score += 1;
    if (/[0-9]/.test(pw) && /[a-zA-Z]/.test(pw)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pw)) score += 1;
    return score;
  })();
  const strengthLabel = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'][strengthScore];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    const result = await register(form.name.trim(), form.email.trim(), form.password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const dest = redirect ? decodeURIComponent(redirect) : HOME_FOR_ROLE[result.user.role] || '/app/dashboard';
    navigate(dest, { replace: true });
  };

  const fieldSx = {
    sx: {
      color: palette.text,
      bgcolor: palette.card,
      '& fieldset': { borderColor: palette.cardBorder },
      '&:hover fieldset': { borderColor: palette.accent },
      '&.Mui-focused fieldset': { borderColor: palette.accent },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: palette.bg }}>
      {/* What a free account gets, in the numbers the server enforces */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          bgcolor: palette.heroBg,
          flexDirection: 'column',
          justifyContent: 'center',
          px: 7,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1.6rem', color: '#fff', mb: 1.5, lineHeight: 1.25 }}>
          Nigerian commodity prices,<br />tracked for you.
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.95rem', mb: 4, maxWidth: 420, lineHeight: 1.6 }}>
          Create a free account and Farmgate will watch the markets you care about
          and email you when a price moves past your level.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {FREE_INCLUDES.map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Icon icon="ph:check-circle-fill" width={18} height={18} color="#3ECF8E" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.9rem' }}>{item}</Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', mt: 4 }}>
          No card needed. Nothing to cancel.
        </Typography>
      </Box>

      {/* Form */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Container maxWidth="xs">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Typography
              component={RouterLink}
              to="/"
              sx={{ display: 'block', mb: 3, fontWeight: 700, fontSize: '1.05rem', color: palette.accent, textDecoration: 'none' }}
            >
              Farmgate
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: palette.text }}>
              Create your free account
            </Typography>
            <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 3 }}>
              Already have one?{' '}
              <Box component={RouterLink} to="/login" sx={{ color: palette.accent, fontWeight: 600, textDecoration: 'none' }}>
                Sign in
              </Box>
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Your name" required fullWidth size="small" autoFocus
                value={form.name} onChange={set('name')} autoComplete="name"
                InputLabelProps={{ sx: { color: palette.textMuted } }}
                InputProps={fieldSx}
              />
              <TextField
                label="Email" type="email" required fullWidth size="small"
                value={form.email} onChange={set('email')} autoComplete="email"
                InputLabelProps={{ sx: { color: palette.textMuted } }}
                InputProps={fieldSx}
              />
              <TextField
                label="Password" type={showPw ? 'text' : 'password'} required fullWidth size="small"
                value={form.password} onChange={set('password')} autoComplete="new-password"
                error={tooShort}
                helperText={tooShort ? `At least ${MIN_LENGTH} characters` : ' '}
                InputLabelProps={{ sx: { color: palette.textMuted } }}
                InputProps={{
                  ...fieldSx,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPw((v) => !v)} edge="end" size="small"
                        aria-label={showPw ? 'Hide password' : 'Show password'}
                        sx={{ color: palette.textMuted }}
                      >
                        {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {form.password.length > 0 && (
                <Box sx={{ mt: -1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(strengthScore / 4) * 100}
                    sx={{
                      height: 4, borderRadius: 2, bgcolor: palette.surfaceHover,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: strengthScore <= 1 ? palette.danger : strengthScore === 2 ? palette.warning : palette.accent,
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted, mt: 0.5 }}>
                    {strengthLabel}
                  </Typography>
                </Box>
              )}

              <Button
                type="submit" variant="contained" disableElevation disabled={!canSubmit}
                endIcon={<East sx={{ fontSize: '16px !important' }} />}
                sx={{
                  bgcolor: palette.accent, color: '#fff', fontWeight: 600, py: 1.2,
                  borderRadius: '10px', textTransform: 'none',
                  '&:hover': { bgcolor: '#1C9550' },
                  '&.Mui-disabled': { bgcolor: palette.cardBorder, color: palette.textMuted },
                }}
              >
                {submitting ? 'Creating your account…' : 'Create free account'}
              </Button>
            </Box>

            <Typography sx={{ mt: 2.5, fontSize: '0.78rem', color: palette.textMuted, lineHeight: 1.6 }}>
              We will email you a link to confirm your address. You can start using
              Farmgate straight away.
            </Typography>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}

function Signup() {
  return (
    <ForceLightTheme>
      <SignupInner />
    </ForceLightTheme>
  );
}

export default Signup;
